import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  DeviceHub as DeviceHubIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  Menu as MenuIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { homologatedDeviceService } from '../services/api';
import Layout from '../components/Layout/Layout';

const DispositivosHomologadosList = () => {
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados para modais
  const [createModal, setCreateModal] = useState({
    open: false,
    type: null, // 'level', 'group', 'device'
    parentId: null
  });
  const [editModal, setEditModal] = useState({
    open: false,
    type: null,
    data: null
  });

  // Estados para formulários
  const [levelForm, setLevelForm] = useState({
    name: '',
    description: '',
    sort_order: 0,
    status: 'active'
  });

  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    level_id: '',
    sort_order: 0,
    status: 'active'
  });

  const [deviceForm, setDeviceForm] = useState({
    name: '',
    description: '',
    manufacturer: '',
    model: '',
    specs: {},
    image_path: '',
    group_id: '',
    status: 'active',
    price_range: '',
    warranty_info: '',
    compatibility_notes: ''
  });

  const [levels, setLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadHierarchy();
  }, []);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      const response = await homologatedDeviceService.getHomologatedHierarchy();
      setHierarchy(response.hierarchy || []);
    } catch (error) {
      console.error('Erro ao carregar hierarquia:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar hierarquia',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNodeToggle = (nodeId) => {
    setExpandedNodes(prev =>
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleCreateClick = (type, parentId = null) => {
    setCreateModal({
      open: true,
      type,
      parentId
    });
  };

  const handleEditClick = (type, data) => {
    setEditModal({
      open: true,
      type,
      data
    });
  };

  const handleDeleteClick = async (type, id) => {
    if (!window.confirm(`Tem certeza que deseja excluir este ${type}?`)) {
      return;
    }

    try {
      if (type === 'level') {
        await homologatedDeviceService.deleteHomologatedLevel(id);
      } else if (type === 'group') {
        await homologatedDeviceService.deleteHomologatedGroup(id);
      } else if (type === 'device') {
        await homologatedDeviceService.deleteHomologatedDevice(id);
      }

      setSnackbar({
        open: true,
        message: `${type} excluído com sucesso`,
        severity: 'success'
      });
      loadHierarchy();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Erro ao excluir ${type}`,
        severity: 'error'
      });
    }
  };

  // Funções para modais
  const handleCreateModalClose = () => {
    setCreateModal({ open: false, type: null, parentId: null });
    // Reset forms
    setLevelForm({ name: '', description: '', sort_order: 0, status: 'active' });
    setGroupForm({ name: '', description: '', level_id: '', sort_order: 0, status: 'active' });
    setDeviceForm({
      name: '', description: '', manufacturer: '', model: '', specs: {},
      image_path: '', group_id: '', status: 'active', price_range: '',
      warranty_info: '', compatibility_notes: ''
    });
    setImageFile(null);
  };

  const handleEditModalClose = () => {
    setEditModal({ open: false, type: null, data: null });
    // Reset forms
    setLevelForm({ name: '', description: '', sort_order: 0, status: 'active' });
    setGroupForm({ name: '', description: '', level_id: '', sort_order: 0, status: 'active' });
    setDeviceForm({
      name: '', description: '', manufacturer: '', model: '', specs: {},
      image_path: '', group_id: '', status: 'active', price_range: '',
      warranty_info: '', compatibility_notes: ''
    });
    setImageFile(null);
  };

  const handleCreateSubmit = async () => {
    setSaving(true);
    try {
      if (createModal.type === 'level') {
        await homologatedDeviceService.createHomologatedLevel(levelForm);
        setSnackbar({ open: true, message: 'Nível criado com sucesso', severity: 'success' });
      } else if (createModal.type === 'group') {
        const formData = { ...groupForm, level_id: createModal.parentId };
        await homologatedDeviceService.createHomologatedGroup(formData);
        setSnackbar({ open: true, message: 'Grupo criado com sucesso', severity: 'success' });
      } else if (createModal.type === 'device') {
        let imagePath = '';
        if (imageFile) {
          const uploadResponse = await homologatedDeviceService.uploadDeviceImage(imageFile);
          imagePath = uploadResponse.image_path;
        }
        const formData = { ...deviceForm, group_id: createModal.parentId, image_path: imagePath };
        await homologatedDeviceService.createHomologatedDevice(formData);
        setSnackbar({ open: true, message: 'Dispositivo criado com sucesso', severity: 'success' });
      }

      handleCreateModalClose();
      loadHierarchy();
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async () => {
    setSaving(true);
    try {
      if (editModal.type === 'level') {
        await homologatedDeviceService.updateHomologatedLevel(editModal.data.id, levelForm);
        setSnackbar({ open: true, message: 'Nível atualizado com sucesso', severity: 'success' });
      } else if (editModal.type === 'group') {
        await homologatedDeviceService.updateHomologatedGroup(editModal.data.id, groupForm);
        setSnackbar({ open: true, message: 'Grupo atualizado com sucesso', severity: 'success' });
      } else if (editModal.type === 'device') {
        let imagePath = deviceForm.image_path;
        if (imageFile) {
          const uploadResponse = await homologatedDeviceService.uploadDeviceImage(imageFile);
          imagePath = uploadResponse.image_path;
        }
        const formData = { ...deviceForm, image_path: imagePath };
        await homologatedDeviceService.updateHomologatedDevice(editModal.data.id, formData);
        setSnackbar({ open: true, message: 'Dispositivo atualizado com sucesso', severity: 'success' });
      }

      handleEditModalClose();
      loadHierarchy();
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao atualizar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Carregar dados para edição
  useEffect(() => {
    if (editModal.open && editModal.data) {
      if (editModal.type === 'level') {
        setLevelForm({
          name: editModal.data.name || '',
          description: editModal.data.description || '',
          sort_order: editModal.data.sort_order || 0,
          status: editModal.data.status || 'active'
        });
      } else if (editModal.type === 'group') {
        setGroupForm({
          name: editModal.data.name || '',
          description: editModal.data.description || '',
          level_id: editModal.data.level_id || '',
          sort_order: editModal.data.sort_order || 0,
          status: editModal.data.status || 'active'
        });
      } else if (editModal.type === 'device') {
        setDeviceForm({
          name: editModal.data.name || '',
          description: editModal.data.description || '',
          manufacturer: editModal.data.manufacturer || '',
          model: editModal.data.model || '',
          specs: editModal.data.specs || {},
          image_path: editModal.data.image_path || '',
          group_id: editModal.data.group_id || '',
          status: editModal.data.status || 'active',
          price_range: editModal.data.price_range || '',
          warranty_info: editModal.data.warranty_info || '',
          compatibility_notes: editModal.data.compatibility_notes || ''
        });
      }
    }
  }, [editModal]);

  // Carregar níveis e grupos para selects
  useEffect(() => {
    const loadSelectData = async () => {
      try {
        const [levelsResponse, groupsResponse] = await Promise.all([
          homologatedDeviceService.getHomologatedLevels(),
          homologatedDeviceService.getHomologatedGroups()
        ]);
        setLevels(levelsResponse.levels || []);
        setGroups(groupsResponse.groups || []);
      } catch (error) {
        console.error('Erro ao carregar dados para selects:', error);
      }
    };

    if (createModal.open || editModal.open) {
      loadSelectData();
    }
  }, [createModal.open, editModal.open]);

  const renderTreeItem = (node, level = 0) => {
    const nodeId = `${node.type}-${node.id}`;
    const isExpanded = expandedNodes.includes(nodeId);
    const hasChildren = node.groups?.length > 0 || node.devices?.length > 0;

    let icon, label, actions;

    if (node.type === 'level') {
      icon = isExpanded ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />;
      label = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {node.name}
          </Typography>
          <Chip
            label={`${node.groups?.length || 0} grupos`}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
      );
      actions = (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => handleCreateClick('group', node.id)}>
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleEditClick('level', node)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteClick('level', node.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    } else if (node.type === 'group') {
      icon = <DeviceHubIcon color="secondary" />;
      label = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {node.name}
          </Typography>
          <Chip
            label={`${node.devices?.length || 0} dispositivos`}
            size="small"
            variant="outlined"
            color="secondary"
          />
        </Box>
      );
      actions = (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => handleCreateClick('device', node.id)}>
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleEditClick('group', node)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteClick('group', node.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    } else if (node.type === 'device') {
      icon = <CheckCircleIcon color="success" />;
      label = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {node.name}
          </Typography>
          {node.manufacturer && (
            <Typography variant="caption" color="text.secondary">
              {node.manufacturer} {node.model}
            </Typography>
          )}
          {node.image_path && (
            <ImageIcon fontSize="small" color="action" />
          )}
        </Box>
      );
      actions = (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => handleEditClick('device', node)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDeleteClick('device', node.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <ListItem
        key={nodeId}
        sx={{
          pl: level * 3,
          borderLeft: level > 0 ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <ListItemButton
          onClick={() => hasChildren && handleNodeToggle(nodeId)}
          sx={{ pr: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {hasChildren ? (
              isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />
            ) : (
              icon
            )}
          </ListItemIcon>
          <ListItemText primary={label} />
        </ListItemButton>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {actions}
        </Box>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.groups?.map(group => renderTreeItem({ ...group, type: 'group' }, level + 1))}
              {node.devices?.map(device => renderTreeItem({ ...device, type: 'device' }, level + 1))}
            </List>
          </Collapse>
        )}
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
          <Typography variant="h4" gutterBottom>
            Carregando Dispositivos Homologados...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
              <CheckCircleIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                Dispositivos Homologados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Catálogo hierárquico de dispositivos aprovados
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreateClick('level')}
          >
            Novo Nível
          </Button>
        </Box>

        {/* Barra de ferramentas */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar dispositivos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => setExpandedNodes(hierarchy.map(level => `level-${level.id}`))}
                >
                  Expandir Tudo
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ChevronRightIcon />}
                  onClick={() => setExpandedNodes([])}
                >
                  Recolher Tudo
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Hierarquia em árvore */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <List>
              {hierarchy.map(level => renderTreeItem({ ...level, type: 'level' }, 0))}
            </List>
            {hierarchy.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Nenhum dispositivo homologado encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comece criando um nível para organizar seus dispositivos
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Modal de Criação */}
        <Dialog
          open={createModal.open}
          onClose={handleCreateModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {createModal.type === 'level' && 'Novo Nível'}
            {createModal.type === 'group' && 'Novo Grupo'}
            {createModal.type === 'device' && 'Novo Dispositivo'}
          </DialogTitle>
          <DialogContent>
            {createModal.type === 'level' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome do Nível"
                    value={levelForm.name}
                    onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={levelForm.description}
                    onChange={(e) => setLevelForm({ ...levelForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Ordem de Classificação"
                    value={levelForm.sort_order}
                    onChange={(e) => setLevelForm({ ...levelForm, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={levelForm.status}
                      onChange={(e) => setLevelForm({ ...levelForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {createModal.type === 'group' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome do Grupo"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Ordem de Classificação"
                    value={groupForm.sort_order}
                    onChange={(e) => setGroupForm({ ...groupForm, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={groupForm.status}
                      onChange={(e) => setGroupForm({ ...groupForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {createModal.type === 'device' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome do Dispositivo"
                    value={deviceForm.name}
                    onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fabricante"
                    value={deviceForm.manufacturer}
                    onChange={(e) => setDeviceForm({ ...deviceForm, manufacturer: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    value={deviceForm.model}
                    onChange={(e) => setDeviceForm({ ...deviceForm, model: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Faixa de Preço"
                    value={deviceForm.price_range}
                    onChange={(e) => setDeviceForm({ ...deviceForm, price_range: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={deviceForm.description}
                    onChange={(e) => setDeviceForm({ ...deviceForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Informações de Garantia"
                    value={deviceForm.warranty_info}
                    onChange={(e) => setDeviceForm({ ...deviceForm, warranty_info: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notas de Compatibilidade"
                    value={deviceForm.compatibility_notes}
                    onChange={(e) => setDeviceForm({ ...deviceForm, compatibility_notes: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Upload de Imagem
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files[0])}
                    />
                  </Button>
                  {imageFile && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Arquivo selecionado: {imageFile.name}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={deviceForm.status}
                      onChange={(e) => setDeviceForm({ ...deviceForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCreateModalClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateSubmit}
              variant="contained"
              disabled={saving}
              startIcon={<SaveIcon />}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Edição */}
        <Dialog
          open={editModal.open}
          onClose={handleEditModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editModal.type === 'level' && 'Editar Nível'}
            {editModal.type === 'group' && 'Editar Grupo'}
            {editModal.type === 'device' && 'Editar Dispositivo'}
          </DialogTitle>
          <DialogContent>
            {editModal.type === 'level' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome do Nível"
                    value={levelForm.name}
                    onChange={(e) => setLevelForm({ ...levelForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={levelForm.description}
                    onChange={(e) => setLevelForm({ ...levelForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Ordem de Classificação"
                    value={levelForm.sort_order}
                    onChange={(e) => setLevelForm({ ...levelForm, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={levelForm.status}
                      onChange={(e) => setLevelForm({ ...levelForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {editModal.type === 'group' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome do Grupo"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Ordem de Classificação"
                    value={groupForm.sort_order}
                    onChange={(e) => setGroupForm({ ...groupForm, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={groupForm.status}
                      onChange={(e) => setGroupForm({ ...groupForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {editModal.type === 'device' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome do Dispositivo"
                    value={deviceForm.name}
                    onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fabricante"
                    value={deviceForm.manufacturer}
                    onChange={(e) => setDeviceForm({ ...deviceForm, manufacturer: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    value={deviceForm.model}
                    onChange={(e) => setDeviceForm({ ...deviceForm, model: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Faixa de Preço"
                    value={deviceForm.price_range}
                    onChange={(e) => setDeviceForm({ ...deviceForm, price_range: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={deviceForm.description}
                    onChange={(e) => setDeviceForm({ ...deviceForm, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Informações de Garantia"
                    value={deviceForm.warranty_info}
                    onChange={(e) => setDeviceForm({ ...deviceForm, warranty_info: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notas de Compatibilidade"
                    value={deviceForm.compatibility_notes}
                    onChange={(e) => setDeviceForm({ ...deviceForm, compatibility_notes: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Alterar Imagem
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files[0])}
                    />
                  </Button>
                  {imageFile && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Novo arquivo: {imageFile.name}
                    </Typography>
                  )}
                  {deviceForm.image_path && !imageFile && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Imagem atual mantida
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={deviceForm.status}
                      onChange={(e) => setDeviceForm({ ...deviceForm, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="active">Ativo</MenuItem>
                      <MenuItem value="inactive">Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditModalClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleEditSubmit}
              variant="contained"
              disabled={saving}
              startIcon={<SaveIcon />}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default DispositivosHomologadosList;
